import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useColorTheme } from "context/ColorThemeContext";
import CustomTextField from "components/TextField";
import DragDropZone from "components/DragDropZone";
import { fetchStickerCreate, fetchStickerUpdate } from "api/stickerService";
import { getImageUrl } from "api/AttachmentAPI";

function AddSticker({ open, onClose, editMode, initialData, refreshDataAfterChange }) {
  const colors = useColorTheme();
  const [form, setForm] = useState({ name: "", displayOrder: 1, status: true, image: null });
  const [responseMessage, setResponseMessage] = useState("");
  const [errors, setErrors] = useState({
    name: "",
    displayOrder: "",
    image: "",
  });

  useEffect(() => {
    const loadFormData = async () => {
      if (editMode && initialData) {
        let image = initialData.imageUrl || null;

        if (initialData.attachment?.encryptedId) {
          try {
            image = await getImageUrl(
              `/api/attachments/downloadFile/${initialData.attachment.encryptedId}`
            );
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error("Lỗi khi tải ảnh:", e);
          }
        }

        setForm({
          name: initialData.stickerName,
          displayOrder: initialData.displayOrder || 1,
          status: initialData.status === 1,
          image,
        });
      } else {
        setForm({ name: "", displayOrder: 1, status: true, image: null });
      }

      setErrors({ name: "", displayOrder: "", image: "" });
    };

    loadFormData();
  }, [editMode, initialData]);

  useEffect(() => {
    if (editMode && initialData) {
      setForm({
        name: initialData.stickerName,
        displayOrder: initialData.displayOrder || 1,
        status: initialData.status === 1,
        image: initialData.imageUrl || null,
      });
    } else {
      setForm({ name: "", displayOrder: 1, status: true, image: null });
    }
    setErrors({ name: "", displayOrder: "", image: "" });
  }, [editMode, initialData]);

  const handleClose = () => {
    setForm({ name: "", displayOrder: 1, status: true, image: null });
    setErrors({ name: "", displayOrder: "", image: "" });
    setResponseMessage("");
    onClose();
  };

  const validateForm = () => {
    const newErrors = {
      name: form.name.trim() ? "" : "Tên sticker là bắt buộc",
      displayOrder: form.displayOrder >= 1 ? "" : "Thứ tự hiển thị phải ≥ 1",
      image: form.image ? "" : "Hình ảnh sticker là bắt buộc",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((msg) => msg === "");
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = new FormData();
    payload.append("stickerName", form.name.trim());
    payload.append("displayOrder", form.displayOrder);
    payload.append("status", form.status ? 1 : 0);
    payload.append("stickerCode", "");

    if (form.image) {
      const file =
        typeof form.image === "string"
          ? new File([await (await fetch(form.image)).blob()], "sticker.jpg")
          : form.image;
      payload.append("image", file);
    }

    let result;
    if (editMode && initialData.stickerId) {
      payload.append("stickerId", initialData.stickerId);
      result = await fetchStickerUpdate(initialData.stickerId, payload);
    } else {
      result = await fetchStickerCreate(payload);
    }

    if (result.success) {
      Swal.fire({
        icon: "success",
        title: editMode ? "Cập nhật thành công" : "Thêm mới thành công",
        showConfirmButton: false,
        timer: 1500,
      });

      refreshDataAfterChange();
      handleClose();
    } else {
      setResponseMessage(result.data?.message || "Có lỗi xảy ra");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          p: 3,
          background: colors.popupBackground || "#f2f2f2",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "24px",
          pb: 0,
          mt: 2,
        }}
      >
        {editMode ? "Chỉnh sửa sticker" : "Thêm mới sticker"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#000",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <CustomTextField
          label="Tên sticker"
          placeholder="Nhập tên sticker"
          value={form.name}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, name: e.target.value }));
            setErrors((prev) => ({ ...prev, name: "" }));
          }}
          fullWidth
          required
          error={Boolean(errors.name)}
          helperText={errors.name}
        />

        <CustomTextField
          label="Thứ tự hiển thị"
          type="number"
          value={form.displayOrder}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, displayOrder: Number(e.target.value) }));
            setErrors((prev) => ({ ...prev, displayOrder: "" }));
          }}
          fullWidth
          required
          error={Boolean(errors.displayOrder)}
          helperText={errors.displayOrder}
        />

        <FormControlLabel
          sx={{ mb: 2, ml: 0 }}
          control={
            <Switch
              checked={form.status}
              onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.checked }))}
            />
          }
          label="Kích hoạt"
        />

        {/* Ảnh sticker */}
        <DragDropZone
          label="Ảnh"
          required
          imagePreview={form.image}
          onDrop={(file) => {
            setForm((prev) => ({ ...prev, image: file }));
            setErrors((prev) => ({ ...prev, image: "" }));
          }}
        />

        {errors.image && (
          <Typography color="error" fontSize="0.85rem" mt={0.5} ml={1.5}>
            {errors.image}
          </Typography>
        )}

        {responseMessage.includes("ảnh") && (
          <Typography color="error" fontSize="0.85rem" mt={0.5} ml={1.5}>
            {responseMessage}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center" }}>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            backgroundColor: colors.buttonBackground || "#2196F3",
            color: colors.popupText || "white",
            borderRadius: "8px",
            paddingX: 4,
            paddingY: 1,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "15px",
            "&:hover": {
              backgroundColor: "#1976D2",
            },
          }}
        >
          {editMode ? "Cập nhật" : "Thêm mới"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddSticker.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  initialData: PropTypes.shape({
    stickerId: PropTypes.number,
    stickerName: PropTypes.string,
    displayOrder: PropTypes.number,
    status: PropTypes.number,
    imageUrl: PropTypes.string,
    attachment: PropTypes.shape({
      encryptedId: PropTypes.string,
    }),
  }),

  refreshDataAfterChange: PropTypes.func.isRequired,
};

AddSticker.defaultProps = {
  editMode: false,
  initialData: null,
};

export default AddSticker;
